import { create } from 'zustand'
import JSZip from 'jszip'

interface GeneratedFile {
  path: string
  content: string
}

interface GeneratedCode {
  id: string
  files: GeneratedFile[]
  warnings?: string[]
}

interface GenerationRequest {
  project_name: string
  environment: string
  aws_region: string
  services: {
    service_id: string
    parameters: Record<string, unknown>
  }[]
}

interface GenerationStore {
  isGenerating: boolean
  generatedCode: GeneratedCode | null
  error: string | null
  generate: (request: GenerationRequest) => Promise<void>
  downloadZip: () => Promise<void>
  clearGeneration: () => void
}

export const useGenerationStore = create<GenerationStore>((set, get) => ({
  isGenerating: false,
  generatedCode: null,
  error: null,

  generate: async (request) => {
    set({ isGenerating: true, error: null })
    try {
      const apiUrl = import.meta.env.VITE_API_URL || ''
      const response = await fetch(`${apiUrl}/api/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      })

      if (!response.ok) {
        throw new Error('Failed to generate Terraform code')
      }

      const data = await response.json()
      set({ generatedCode: data, isGenerating: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Unknown error',
        isGenerating: false,
      })
    }
  },

  downloadZip: async () => {
    const { generatedCode } = get()
    if (!generatedCode) return

    const zip = new JSZip()
    for (const file of generatedCode.files) {
      zip.file(file.path, file.content)
    }

    const blob = await zip.generateAsync({ type: 'blob' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'terraform-code.zip'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  },

  clearGeneration: () => set({ generatedCode: null, error: null }),
}))
